import {Box, Radio, Tab, Tabs, Typography} from '@mui/material';
import {t} from 'i18next';
import {useRef, useState} from 'react';
import './ViewTemplates.scss';
import ArrDataGrid from '../../../common/arr-data-grid/ArrDataGrid';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as MoreIcon} from '../../../../assets/images/icon-more.svg';
import {ReactComponent as CloseToast} from '../../../../../src/assets/images/close-toast.svg';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {PAGE_SIZE, TOAST_DURATION} from '../../../../shared/constants';
import {GridRenderCellParams, GridSortModel} from '@mui/x-data-grid';
import {
  manageTemplateDetailsMoreMenu,
  manageTemplatesColumns,
  getViewTemplateTabs,
} from '../manageTemplatesUtils';
import {
  useDeleteManageTemplate,
  useGetManageTemplates,
  useSecurityGroupDefaultSaveTemplate,
} from '../../../../hooks/productions/useProductions';
import {GetManageTemplatesRequestParam} from '../../../../api/types/production.type';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrToast from '../../../common/arr-toast/ArrToast';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as RadioEnableIcon} from '../../../../assets/images/radio-enable-icon.svg';
import {ReactComponent as RadioActiveIcon} from '../../../../assets/images/radio-active-icon.svg';
import {formatDateTime} from '../../../../utils/utils';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../common/arr-button/ArrButton';
import {ChevronLeft} from '@mui/icons-material';
import PrivGuard from '../../create-new-production/priv-guard/PrivGuard';
import ProductionComponents from '../../create-new-production/production-components/ProductionComponents';
import NumberingSorting from '../../create-new-production/numbering-sorting/NumberingSorting';
import {FormMode} from '../../create-new-production/CreateNewProductionUtils';

interface ViewTemplatesProps {
  securityGroupId: number;
}

const ViewTemplates: React.FC<ViewTemplatesProps> = ({securityGroupId}) => {
  const [showToast, setShowToast] = useState<boolean>(false);
  const [showSecurityToast, setShowSecurityToast] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'templateName',
      sort: 'asc',
    },
  ]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>(0);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');
  const [isDeleteManageTemplateModal, setIsDeleteManageTemplateModal] =
    useState<boolean>(false);
  const [isSecurityGroupTemplateModal, setIsSecurityGroupTemplateModal] =
    useState<boolean>(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState<boolean>(false);
  const [viewTemplateTabValue, setViewTemplateTabValue] = useState(0);

  const menuRef = useRef<ArrMenuHandle | null>(null);

  // securityGroupId is provided by parent; no need to fetch here

  const {
    data: deleteManageTemplateData,
    mutate: deleteManageTemplate,
    error: deleteManageTemplateError,
  } = useDeleteManageTemplate();

  const {
    data: securityGroupDefultSaveTemplateData,
    mutate: securityGroupDefultSaveTemplate,
    error: securityGroupDefultSaveTemplateError,
  } = useSecurityGroupDefaultSaveTemplate();

  const {
    data: manageTemplatesListData,
    isLoading: isManageTemplatesLoading,
    refetch: refetchManageTemplates,
  } = useGetManageTemplates({
    pageNumber: pageNumber,
    pageSize: PAGE_SIZE,
    securityGroupId: securityGroupId,
    isTemplateTypeCustom: true,
    sortColumn: sortModel[0]?.field || 'templateId',
    sortDirection: sortModel[0]?.sort || 'asc',
  } as GetManageTemplatesRequestParam);

  const manageTemplatesWithTempId = manageTemplatesListData?.templatesList.map(
    (u, index) => ({
      ...u,
      tempId: index + 1,
    })
  );

  const updatedColumns = [
    ...manageTemplatesColumns.map((col) => {
      if (col.field === 'isSecurityGroupTemplate') {
        return {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const {templateId} = params.row;
            const isSelected = params.value === true;
            return (
              <Radio
                slotProps={{
                  input: {
                    'aria-label': String(col.field),
                    'data-testid': `radio-${col.field}`,
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
                className="radio-group"
                checked={isSelected}
                onChange={() => {
                  setSelectedTemplateId(templateId);
                  setIsSecurityGroupTemplateModal(true);
                }}
                icon={<RadioEnableIcon />}
                checkedIcon={<RadioActiveIcon />} // Optional: Use different icon for checked
                data-testid={`sec-group-template-${params.id}`}
              />
            );
          },
        };
      }
      if (col.field === 'dateCreated') {
        return {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const formattedDateCreated = formatDateTime(params.value);
            return (
              <span data-testid={`date-created-${params.id}`}>
                {formattedDateCreated === '--'
                  ? ''
                  : formattedDateCreated || ' '}
              </span>
            );
          },
        };
      }
      if (col.field === 'lastModifiedDate') {
        return {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const formattedLastModified = formatDateTime(params.value);
            return (
              <span data-testid={`datemodified-${params.id}`}>
                {formattedLastModified || ' '}
              </span>
            );
          },
        };
      }

      return col;
    }),
    {
      field: 'action',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      headerClassName: 'typo-text label2_size more-option-header',
      renderCell: (params: GridRenderCellParams) => {
        const {templateId, templateName} = params.row;
        return (
          <ArrIconButton
            onClick={(e) => handleMoreIconClick(e, templateId, templateName)}
            icon={<MoreIcon />}
            ariaLabel=""
            shape="none"
            className="templates-grid-edit-icon"
            testId={`view-templates-action-button-${params.id}`}
          />
        );
      },
    },
  ];

  const onPageChangeHandler = (value: number) => {
    setPageNumber(value);
  };
  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleMoreIconClick = (
    e: React.MouseEvent<HTMLElement>,
    templateId: number,
    templateName: string
  ) => {
    menuRef.current?.open(e.currentTarget as HTMLElement);
    setSelectedTemplateId(templateId);
    setSelectedTemplateName(templateName);
  };

  const onMenuItemClick = (item: CustomMenuItem) => {
    if (item.id === 'view') {
      setIsViewPanelOpen(true);
      setIsDeleteManageTemplateModal(false);
    } else if (item.id === 'delete') {
      setIsDeleteManageTemplateModal(true);
      setIsViewPanelOpen(false);
    }
  };

  const ConfirmDeleteManageTemplate = () => {
    deleteManageTemplate(selectedTemplateId, {
      onSuccess: () => {
        if (pageNumber !== 1) {
          setPageNumber(1);
        } else {
          refetchManageTemplates();
        }
        setShowToast(true);
      },
      onError: () => {
        setShowToast(true);
      },
    });

    setIsDeleteManageTemplateModal(false);
  };

  const ConfirmSecurityGroupTemplate = () => {
    const defaultSavePayload = {
      templateId: selectedTemplateId,
      isTemplateTypeCustom: true,
      securityGroupId: securityGroupId || 0,
    };
    securityGroupDefultSaveTemplate(defaultSavePayload, {
      onSuccess: () => {
        if (pageNumber !== 1) {
          setPageNumber(1);
        } else {
          refetchManageTemplates();
        }
        setShowSecurityToast(true);
      },
      onError: () => {
        setShowSecurityToast(true);
      },
    });

    setIsSecurityGroupTemplateModal(false);
  };

  const handleViewTemplateTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setViewTemplateTabValue(newValue);
  };

  const viewTemplateTabs = getViewTemplateTabs();

  const viewTemplateContent = (
    <Box>
      <Box className="view-template-panel-container">
        <Box className="view-template-panel-header">
          <Box className="view-template-panel-title">
            <Typography
              className=" typo-text headline3_size title"
              data-testid={`view-templates-selected-template-name`}
            >
              {selectedTemplateName}
            </Typography>
          </Box>
          <Box className="view-template-action-buttons">
            {viewTemplateTabValue > 0 && (
              <ArrButton
                dataTestId="view-template-back-button"
                variant="secondary"
                size="medium"
                startIcon={<ChevronLeft />}
                onClick={() =>
                  setViewTemplateTabValue(viewTemplateTabValue - 1)
                }
              >
                {t('common.back')}
              </ArrButton>
            )}
            {viewTemplateTabValue < viewTemplateTabs.length - 1 && (
              <ArrButton
                dataTestId="view-template-next-button"
                variant="primary"
                size="medium"
                onClick={() =>
                  setViewTemplateTabValue(viewTemplateTabValue + 1)
                }
              >
                {t('common.next')}
              </ArrButton>
            )}
          </Box>
        </Box>
        <Box className="view-template-panel-content">
          <Tabs
            value={viewTemplateTabValue}
            onChange={handleViewTemplateTabChange}
            aria-label="View Templates"
            className="view-template-panel-tabs"
            data-testid="view-template-panel-tabs"
          >
            {viewTemplateTabs.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                className="typo-text subhead4_size view-template-panel-tab-item"
                data-testid={`view-template-panel-tab-item-${index}`}
              />
            ))}
          </Tabs>
          <Box className="view-template-panel-tab-content">
            {viewTemplateTabValue === 0 && (
              <PrivGuard
                isProductionSet={true}
                productionId={0}
                productionName={''}
                mode={FormMode.VIEW}
                isTemplate={true}
                templateId={selectedTemplateId}
              />
            )}
            {viewTemplateTabValue === 1 && (
              <ProductionComponents
                isProductionSet={true}
                productionId={0}
                exportProductionId={0}
                mode={FormMode.VIEW}
                isTemplate={true}
                templateId={selectedTemplateId}
              />
            )}
            {viewTemplateTabValue === 2 && (
              <NumberingSorting
                isProductionSet={true}
                productionId={0}
                productionName={''}
                mode={FormMode.VIEW}
                isTemplate={true}
                templateId={selectedTemplateId}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box className="view-template-container">
      <Box className="view-template-header">
        <Box className="view-template-title">
          <Typography
            className="view-template-title-text"
            data-testid={`view-templates-header-title`}
          >
            {t('productionsAndExports.manageTemplates.headerTitle')}
          </Typography>
          <ArrTooltipWrapper
            title={t('productionsAndExports.manageTemplates.headerTitle')}
            trigger={<InfoIcon fontSize="medium" className="info-icon" />}
          >
            {t('productionsAndExports.manageTemplates.headerInfo')}
          </ArrTooltipWrapper>
        </Box>
      </Box>
      <ArrDataGrid
        rows={manageTemplatesWithTempId || []}
        columns={updatedColumns}
        rowCount={manageTemplatesListData?.totalRecords}
        currentPage={pageNumber}
        itemsPerPage={PAGE_SIZE}
        paginationMode="server"
        onPageChange={onPageChangeHandler}
        entityType={t('common.entries')}
        primaryKey="tempId"
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        className="view-templates-grid"
        loading={isManageTemplatesLoading}
        noRowsOverlay={() => (
          <Box className="no-grid-overlay">
            <Typography
              className="typo-text body3_size"
              data-testid={`view-templates-no-results`}
            >
              {t('productionsAndExports.manageTemplates.noResults')}
            </Typography>
          </Box>
        )}
      />
      <ArrMenu
        id="more-menu"
        ref={menuRef}
        items={manageTemplateDetailsMoreMenu}
        onItemClick={onMenuItemClick}
        direction="left"
      />
      {isDeleteManageTemplateModal && (
        <ArrModal
          showModal={isDeleteManageTemplateModal}
          showClose={false}
          onClose={() => setIsDeleteManageTemplateModal(false)}
          title={t('productionsAndExports.manageTemplates.deletePopup.title')}
          description={t(
            'productionsAndExports.manageTemplates.deletePopup.description'
          )}
          dataTestId="delete-manage-template-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => setIsDeleteManageTemplateModal(false),
              variant: 'secondary',
              dataTestId: 'dmt-cancel-btn',
            },
            {
              id: '2',
              label: t(
                'productionsAndExports.manageTemplates.deletePopup.confirmButton'
              ),
              onClick: ConfirmDeleteManageTemplate,
              variant: 'danger',
              dataTestId: 'dmt-del-btn',
            },
          ]}
        />
      )}
      {showToast && (
        <ArrToast
          id="detele-template-toast"
          title={
            securityGroupDefultSaveTemplateError || deleteManageTemplateError
              ? t('common.error')
              : t('common.success')
          }
          message={
            deleteManageTemplateError?.message ||
            deleteManageTemplateData?.message
          }
          type={deleteManageTemplateError ? 'error' : 'success'}
          duration={TOAST_DURATION}
          onClose={() => setShowToast(false)}
          dismissable={true}
          closeIcon={<CloseToast />}
          position="bottom-right"
        />
      )}
      {showSecurityToast && (
        <ArrToast
          id="detele-template-toast"
          title={
            securityGroupDefultSaveTemplateError
              ? t('common.error')
              : t('common.success')
          }
          message={
            securityGroupDefultSaveTemplateError?.message ||
            securityGroupDefultSaveTemplateData?.message
          }
          type={securityGroupDefultSaveTemplateError ? 'error' : 'success'}
          duration={TOAST_DURATION}
          onClose={() => setShowSecurityToast(false)}
          dismissable={true}
          closeIcon={<CloseToast />}
          position="bottom-right"
        />
      )}
      {isSecurityGroupTemplateModal && (
        <ArrModal
          showModal={isSecurityGroupTemplateModal}
          showClose={false}
          onClose={() => setIsSecurityGroupTemplateModal(false)}
          title={t(
            'productionsAndExports.manageTemplates.securityGroupPopup.title'
          )}
          description={t(
            'productionsAndExports.manageTemplates.securityGroupPopup.description'
          )}
          dataTestId="securtiy-group-template-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => setIsSecurityGroupTemplateModal(false),
              variant: 'secondary',
              dataTestId: 'sgt-cancel-btn',
            },
            {
              id: '2',
              label: t(
                'productionsAndExports.manageTemplates.securityGroupPopup.confirmButton'
              ),
              onClick: ConfirmSecurityGroupTemplate,
              variant: 'primary',
              dataTestId: 'sgt-confirm-btn',
            },
          ]}
        />
      )}
      {isViewPanelOpen && (
        <ArrDrawer
          side="right"
          isOpen={isViewPanelOpen}
          showButton={true}
          size="large"
          onClose={() => {
            setViewTemplateTabValue(0);
            setIsViewPanelOpen(false);
          }}
          className="view-template"
        >
          {viewTemplateContent}
        </ArrDrawer>
      )}
    </Box>
  );
};

export default ViewTemplates;
